import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetTrigger, SheetClose,
} from "@/components/ui/sheet";
import {
  LayoutDashboard, Sparkles, Wallet, Package, ClipboardList,
  Truck, Utensils, BookOpen, CalendarDays, Users, Clock, LogOut, ClipboardCheck,
  BarChart3, FileText, TrendingUp, Megaphone, ShieldCheck, Heart, LineChart as LineChartIcon,
  Map, Globe, Settings, History, Landmark, FolderArchive, Target, CheckSquare, Activity,
  Menu as MenuIcon,
} from "lucide-react";

/**
 * FASE 7.4 STEP E — Nav items with permission gating.
 *
 * `permission` and `anyPermissions` mirror ProtectedRoute props. Items are
 * filtered client-side using `can()` / `canAny()` from AuthContext.
 * NEVER hardcode role checks here — the backend registry stays the source of
 * truth (see services/permissions.py).
 */
const items = [
  { to: "/",                    label: "Dashboard",             icon: LayoutDashboard, testid: "nav-dashboard",             permission: "dashboard.view_basic" },
  { to: "/ai-manager",          label: "AI Manager",            icon: Sparkles,        testid: "nav-ai-manager",            permission: "ai_manager.use" },
  { to: "/finance",             label: "Finanze",               icon: Wallet,          testid: "nav-finance",               permission: "finance.read" },
  { to: "/reconciliation",      label: "Riconciliazione",       icon: Landmark,        testid: "nav-reconciliation",        permission: "bank.reconcile" },
  { to: "/documents",           label: "Documenti",             icon: FolderArchive,   testid: "nav-documents",             anyPermissions: ["documents.read.all", "documents.read.haccp_only"] },
  { to: "/inventory",           label: "Magazzino",             icon: Package,         testid: "nav-inventory",             permission: "inventory.read" },
  { to: "/inventory-count",     label: "Inventario",            icon: ClipboardCheck,  testid: "nav-inventory-count",       permission: "inventory.count" },
  { to: "/suppliers",           label: "Fornitori",             icon: Truck,           testid: "nav-suppliers",             permission: "inventory.read" },
  { to: "/orders",              label: "Ordini",                icon: ClipboardList,   testid: "nav-orders",                permission: "orders.read" },
  { to: "/recipes",             label: "Ricette",               icon: BookOpen,        testid: "nav-recipes",               permission: "recipes.read" },
  { to: "/menu",                label: "Menu",                  icon: Utensils,        testid: "nav-menu",                  permission: "menu.read" },
  { to: "/tasks",               label: "Agenda",                icon: CalendarDays,    testid: "nav-tasks",                 permission: "tasks.read" },
  { to: "/employees",           label: "Personale",             icon: Users,           testid: "nav-employees",             permission: "employees.read_basic" },
  { to: "/shifts",              label: "Turni",                 icon: Clock,           testid: "nav-shifts",                permission: "shifts.read" },
  { to: "/sales",               label: "Vendite",               icon: BarChart3,       testid: "nav-sales",                 permission: "sales.read" },
  { to: "/daily-close",         label: "Chiusura giornata",     icon: CheckSquare,     testid: "nav-daily-close",           permission: "daily_close.read" },
  { to: "/data-quality",        label: "Qualità dati",          icon: Activity,        testid: "nav-data-quality",          permission: "data_quality.view" },
  { to: "/product-performance", label: "Performance Prodotti",  icon: TrendingUp,      testid: "nav-product-performance",   permission: "sales.read" },
  { to: "/menu-engineering",    label: "Menu Engineering",      icon: Target,          testid: "nav-menu-engineering",      permission: "dashboard.view_financial" },
  { to: "/reports",             label: "Report PDF",            icon: FileText,        testid: "nav-reports",               permission: "reports.export" },
  { to: "/simulations",         label: "Simulazioni",           icon: TrendingUp,      testid: "nav-simulations",           permission: "simulations.run" },
  { to: "/marketing",           label: "Marketing",             icon: Megaphone,       testid: "nav-marketing",             permission: "marketing.write" },
  { to: "/haccp",               label: "HACCP",                 icon: ShieldCheck,     testid: "nav-haccp",                 permission: "haccp.execute" },
  { to: "/crm",                 label: "CRM Clienti",           icon: Heart,           testid: "nav-crm",                   permission: "crm.read" },
  { to: "/forecast",            label: "Forecast",              icon: LineChartIcon,   testid: "nav-forecast",              permission: "forecast.view" },
  { to: "/competitors",         label: "Concorrenza",           icon: Map,             testid: "nav-competitors",           permission: "marketing.write" },
  { to: "/menu-i18n",           label: "Menu QR",               icon: Globe,           testid: "nav-menu-i18n",             permission: "menu.translate" },
  { to: "/reorder",             label: "Riordino Intelligente", icon: Package,         testid: "nav-reorder",               permission: "orders.write" },
  { to: "/staffing",            label: "Fabbisogno Personale",  icon: Clock,           testid: "nav-staffing",              permission: "dashboard.view_financial" },
  { to: "/economic-config",     label: "Config. Economica",     icon: Target,          testid: "nav-economic-config",       permission: "settings.write.financial" },
  { to: "/admin",               label: "Amministrazione",       icon: Settings,        testid: "nav-admin",                 permission: "users.read" },
  { to: "/audit",               label: "Audit Log",             icon: History,         testid: "nav-audit",                 permission: "audit.read" },
];

/**
 * NavList — single source of truth for both desktop sidebar and mobile drawer.
 * `onNavigate` is called after each click so the mobile drawer can close itself.
 */
function NavList({ can, canAny, onNavigate, testidPrefix = "" }) {
  return (
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
      {items.map((it) => {
        // FASE 7.4 STEP E — permission-based filter (both desktop + mobile).
        if (it.permission && !can(it.permission)) return null;
        if (it.anyPermissions && !canAny(it.anyPermissions)) return null;
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            onClick={onNavigate}
            data-testid={`${testidPrefix}${it.testid}`}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            <Icon size={16} strokeWidth={1.5} />
            <span>{it.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function SidebarInner({ user, can, canAny, onLogout, onNavigate, testidPrefix = "" }) {
  return (
    <>
      <div className="px-5 py-6 border-b border-[#E2D9C8] flex flex-col items-center text-center shrink-0">
        <img src="/logo-carasau-dark.png" alt="Carasau Café" className="w-32 md:w-36 h-auto" />
        <div className="label-eyebrow mt-3">Orosei · Sardegna</div>
      </div>
      <NavList can={can} canAny={canAny} onNavigate={onNavigate} testidPrefix={testidPrefix} />
      <div className="border-t border-[#E2D9C8] p-4 shrink-0">
        <div className="text-xs text-neutral-500 mb-1 truncate">{user?.email}</div>
        <div className="text-xs uppercase tracking-widest text-neutral-400 mb-3">{user?.role}</div>
        <button
          data-testid={`${testidPrefix}logout-button`}
          onClick={onLogout}
          className="w-full flex items-center gap-2 justify-center text-sm py-2 px-3 rounded-md border border-neutral-900 hover:bg-neutral-900 hover:text-[#FDFBF7] transition-colors min-h-[40px]"
        >
          <LogOut size={14} strokeWidth={1.5} /> Esci
        </button>
      </div>
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout, can, canAny } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleLogout = async () => { await logout(); navigate("/login"); };
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-neutral-900 md:flex">
      {/* --------- MOBILE TOP BAR ( < md ) --------- */}
      <header
        className="md:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-4 h-14 border-b border-[#E2D9C8] bg-[#FDFBF7]"
        data-testid="mobile-topbar"
      >
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-nav-trigger"
              aria-label="Apri menu"
              className="inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#E2D9C8] active:bg-[#F3EDE1]"
            >
              <MenuIcon size={18} strokeWidth={1.5} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-[85%] max-w-[320px] p-0 flex flex-col bg-[#FDFBF7]"
            data-testid="mobile-nav-drawer"
          >
            <SheetClose className="hidden" />
            <SidebarInner
              user={user}
              can={can}
              canAny={canAny}
              onLogout={handleLogout}
              onNavigate={closeMobile}
              testidPrefix="mobile-"
            />
          </SheetContent>
        </Sheet>
        <img src="/logo-carasau-dark.png" alt="Carasau Café" className="h-8 w-auto" />
        <div className="w-10" aria-hidden="true" /> {/* balance flex spacing */}
      </header>

      {/* --------- DESKTOP SIDEBAR ( >= md ) --------- */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-[#E2D9C8] bg-[#FDFBF7] flex-col sticky top-0 h-screen">
        <SidebarInner user={user} can={can} canAny={canAny} onLogout={handleLogout} />
      </aside>

      {/* --------- MAIN CONTENT --------- */}
      <main className="flex-1 min-w-0 w-full">
        <div className="max-w-[1400px] mx-auto px-4 py-5 md:px-8 md:py-8 min-w-0">
          {children}
        </div>
      </main>
    </div>
  );
}
